import React from 'react';

import { Box, Image, Text, Card, Button } from '@opendoor/bricks/core';
import { getHubBuyingFeedRoute, getHubBuyingSearchRoute } from 'shared/helpers/routes';

import {
  MIN_PROPERTY_CARD_HEIGHT,
  MOBILE_PROPERTY_CARD_WIDTH,
} from 'buying/components/propertyCards/PropertyCard';
import { PropertyCard as PropertyCardProps } from 'buying/components/propertyCards/PropertyCardRow';
import { PROPERTY_PLACEHOLDER_IMAGE } from 'buying/helpers/images';
import { GetFavoritePropertiesForCurrentUser_buyer_getFavoritePropertiesForCurrentUser_properties_photos as FavortitePropertiesPhotosProps } from 'buying/graphql/__generated__/GetFavoritePropertiesForCurrentUser';
import { GetFeedPropertiesForCurrentUser_buyer_getFeedPropertiesForCurrentUser_properties_photos as FeedPropertiesPhotosProps } from 'buying/graphql/__generated__/GetFeedPropertiesForCurrentUser';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import { useQualificationContext } from 'shared/helpers/QualificationContext';
import { generatePath, useHistory } from 'react-router-dom';

type ViewMoreCardProps = {
  popularPropertiesRedirect: boolean;
  feedPropertiesCount?: string;
  market?: string;
  staticCarouselProperties: PropertyCardProps[];
};

export type PropertyPhotoProps =
  | (FeedPropertiesPhotosProps | null)[]
  | (FavortitePropertiesPhotosProps | null)[]
  | null;

/** "View more" card for PropertyCardRow */
export const ViewMoreCard: React.FC<ViewMoreCardProps> = ({
  popularPropertiesRedirect,
  feedPropertiesCount,
  market,
  staticCarouselProperties,
}) => {
  const { addressUUID } = useQualificationContext();
  const { isMobile, isSmallTablet, isTablet } = useDevice();
  const isTabletScreenSizeOrSmaller: boolean = isMobile || (isSmallTablet && isTablet);
  const propertyPhotos = staticCarouselProperties.map((p) => p?.property?.photos);
  const history = useHistory();

  const seeMoreListingsLink = () =>
    history.push(
      generatePath(
        popularPropertiesRedirect ? getHubBuyingSearchRoute() : getHubBuyingFeedRoute(),
        { addressUUID },
      ),
    );

  const seeMorePropertiesCard = (
    <Card
      width={isMobile ? MOBILE_PROPERTY_CARD_WIDTH : '100%'}
      height="100%"
      minHeight={MIN_PROPERTY_CARD_HEIGHT}
      variant="secondary"
      padding="16px"
      paddingTop={[null, null, '24px', '32px']}
      paddingBottom={[null, null, '24px', '32px']}
      data-testid="see-more-properties-card"
    >
      <Text
        size={isTabletScreenSizeOrSmaller ? 's1' : 's2'}
        color="neutrals100"
        fontWeight={isTabletScreenSizeOrSmaller ? 'semibold' : 'bold'}
        mb={`${!isTabletScreenSizeOrSmaller ? '15' : '0'}px`}
        data-testid="more-properties-header"
      >
        {popularPropertiesRedirect ? 'View ' : 'There are '}
        <Text
          size={isTabletScreenSizeOrSmaller ? 's1' : 's2'}
          color="brand50"
          fontWeight={isTabletScreenSizeOrSmaller ? 'semibold' : 'bold'}
          display="inline"
          data-testid="more-properties-count"
        >
          {popularPropertiesRedirect ? '' : feedPropertiesCount}
        </Text>{' '}
        more {popularPropertiesRedirect ? 'popular' : ''} homes for sale in {market}.
      </Text>
      <ViewMoreCardImageCarousel propertyPhotos={propertyPhotos} isMobile={isMobile} />
      <Button
        analyticsName="cake-property-card-see-more-listings"
        aria-label={`View ${popularPropertiesRedirect ? '' : feedPropertiesCount} ${
          popularPropertiesRedirect ? 'popular' : 'recent'
        } properties in ${market}`}
        variant="secondary"
        onClick={seeMoreListingsLink}
        data-testid="see-more-listings-button"
      >
        See more listings
      </Button>
    </Card>
  );

  return seeMorePropertiesCard;
};

type ViewMoreCardImageCarouselProps = {
  propertyPhotos: PropertyPhotoProps[];
  isMobile?: boolean;
};

/** Static image carousel below ViewMoreCard main header */
const ViewMoreCardImageCarousel: React.FC<ViewMoreCardImageCarouselProps> = ({
  propertyPhotos,
  isMobile,
}) => {
  const mobileCarouselLength = 3;

  return (
    <Box
      height="100%"
      position="relative"
      display="flex"
      overflowX="hidden"
      mt={['25px', null, '5px', '15px']}
      mr="-16px"
    >
      <Box
        height="100%"
        position="absolute"
        backgroundImage="linear-gradient(to left, #ffffffcf, #ffffff10)"
        width="50%"
        top="0"
        right="0"
        display="block"
      />
      {(isMobile && propertyPhotos.length > 0
        ? propertyPhotos.slice(0, mobileCarouselLength)
        : propertyPhotos
      ).map((propertyPhoto: PropertyPhotoProps, i: number) => {
        return <PropertyImage propertyPhoto={propertyPhoto} key={i} id={i} />;
      })}
    </Box>
  );
};

type PropertyImageProps = {
  propertyPhoto: PropertyPhotoProps;
  key?: number;
  id?: number;
};

const PropertyImage: React.FC<PropertyImageProps> = ({ propertyPhoto, id }) => {
  const photo =
    propertyPhoto && propertyPhoto.length > 0
      ? propertyPhoto[0]?.urls?.size768x512
      : PROPERTY_PLACEHOLDER_IMAGE;

  return (
    <Image
      src={photo}
      alt="Static image of property"
      borderRadius="semiRounded"
      width="75px"
      maxHeight={['75px', '75px', '58px', '70px']}
      data-testid="photo-view-more-static-carousel"
      flexShrink={0}
      boxShadow="16px 5px 12px #E9E9E9"
      mr={['10px', null, null, '6px']}
      ml="5px"
      key={id}
    />
  );
};
